import React from 'react';
import { Users, CheckCircle, Clock, Building2, AlertTriangle, TrendingUp } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';
import RecentActivities, { RecentActivitiesSkeleton } from './RecentActivities';

const Dashboard: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  // Fetch dashboard statistics
  const statsQuery = $api.useQuery(
    'get',
    '/api/organizations/{organization_id}/stats',
    {
      params: {
        path: {
          organization_id: orgId,
        },
      },
    },
    {
      enabled: !!orgId,
    }
  );

  const stats = statsQuery.data;

  // TODO: pendingVerifications - Not available in API yet
  const pendingVerifications = 0; // TODO: API endpoint needed for pending verifications count

  // TODO: completedVerifications - Not available in API yet
  const completedVerifications = 0; // TODO: API endpoint needed for completed verifications count

  // TODO: Verifications (7j) - Not available in API yet
  const verificationsLast7Days = 0; // TODO: API endpoint needed for verifications count in last 7 days

  // Skeleton loader component
  const StatCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-lg bg-gray-200 animate-pulse">
          <div className="h-6 w-6 bg-gray-300 rounded"></div>
        </div>
      </div>
      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
      <div className="h-8 w-16 bg-gray-300 rounded animate-pulse"></div>
    </div>
  );

  const PerformanceCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-4">
        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-12 bg-gray-300 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    </div>
  );


  if (statsQuery.isLoading || !orgId) {
    return (
      <div className="p-6">
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>

        {/* Performance Sections Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <PerformanceCardSkeleton key={i} />
          ))}
        </div>

        {/* Recent Activity Skeleton */}
        <RecentActivitiesSkeleton />
      </div>
    );
  }

  if (statsQuery.error || !stats) {
    return (
      <div className="p-6">
        <ErrorMessage error={statsQuery.error || 'Impossible de charger les statistiques'} />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Entités Totales',
      value: stats.entities_count.toLocaleString(),
      icon: Users,
      color: 'blue'
    },
    {
      title: 'Entreprises',
      value: stats.companies_count.toLocaleString(),
      icon: Building2,
      color: 'purple'
    },
    {
      title: 'Vérifications En Attente',
      value: pendingVerifications.toLocaleString(), // TODO: Use API value when available
      icon: Clock,
      color: 'yellow'
    },
    {
      title: 'Vérifications Complétées',
      value: completedVerifications.toLocaleString(), // TODO: Use API value when available
      icon: CheckCircle,
      color: 'green'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-500 text-blue-600 bg-blue-50',
    purple: 'bg-purple-500 text-purple-600 bg-purple-50',
    yellow: 'bg-yellow-500 text-yellow-600 bg-yellow-50',
    green: 'bg-green-500 text-green-600 bg-green-50',
    red: 'bg-red-500 text-red-600 bg-red-50'
  };

  return (
    <div className="p-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const colors = colorClasses[stat.color as keyof typeof colorClasses].split(' ');
          
          const isEntitiesCard = stat.title === 'Entités Totales';
          const isSandboxMode = currentOrganization?.sandbox_mode;
          const showSandboxLimit = isEntitiesCard && isSandboxMode;
          
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${colors[2]}`}>
                  <Icon className={`h-6 w-6 ${colors[1]}`} />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{stat.title}</h3>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              {showSandboxLimit && (
                <p className="text-xs text-amber-600 mt-2">
                  Limite sandbox : 50 entités
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* KYC Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Alertes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span>Alertes</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">En attente</span>
              <span className="text-sm font-medium text-red-600">{stats.pending_alerts_count}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Résolues (30j)</span>
              <span className="text-sm font-medium text-green-600">{stats.alerts_resolved_last_30d_count}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Temps moyen de traitement</span>
              <span className="text-sm font-medium text-blue-600">{stats.average_time_before_alert_checked_hours.toFixed(1)}h</span>
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            <span>Performance</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de vérification</span>
              <span className="text-sm font-medium text-green-600">
                {/* TODO: Calculate verification rate when pendingVerifications and completedVerifications are available in API */}
                {completedVerifications + pendingVerifications > 0 
                  ? Math.round((completedVerifications / (completedVerifications + pendingVerifications)) * 100)
                  : 0}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Entités par entreprise</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.companies_count > 0 
                  ? (stats.entities_count / stats.companies_count).toFixed(1)
                  : '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Activité */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Clock className="h-5 w-5 text-green-500" />
            <span>Activité</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Nouvelles entités (7j)</span>
              <span className="text-sm font-medium text-blue-600">{stats.entities_added_last_7d_count}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Vérifications (7j)</span>
              <span className="text-sm font-medium text-green-600">
                {/* TODO: Use API value when available */}
                {verificationsLast7Days}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Alertes résolues (7j)</span>
              <span className="text-sm font-medium text-gray-900">{stats.alerts_resolved_last_7d_count}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <RecentActivities />
    </div>
  );
};

export default Dashboard;